import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { FiAlertCircle, FiCheck, FiCheckCircle, FiCreditCard } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import ApiService, { getApiErrorMessage } from '../../services/api';
import PaymentService from '../../services/paymentService';

const CYCLES = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'annually', label: 'Annually' },
];

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Subscription = () => {
  const { user } = useAuth();
  const access = user?.agentAccess || {};

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await ApiService.get('/membership-plans', { audience: 'agent' });
        const list = Array.isArray(res?.data) ? res.data : [];
        const agentPlan = list.find((p) => p.name === 'agent') || list[0] || null;
        if (active) setPlan(agentPlan);
      } catch (error) {
        if (active) toast.error(getApiErrorMessage(error, 'Failed to load subscription plan'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const availableCycles = useMemo(() => {
    const pricing = plan?.pricing || {};
    return CYCLES.filter((c) => Number(pricing[c.id]) > 0);
  }, [plan]);

  useEffect(() => {
    if (availableCycles.length > 0 && !availableCycles.some((c) => c.id === billingCycle)) {
      setBillingCycle(availableCycles[0].id);
    }
  }, [availableCycles, billingCycle]);

  const amount = Number(plan?.pricing?.[billingCycle] || 0);
  const priceConfigured = availableCycles.length > 0;
  const highlights = Array.isArray(plan?.featureHighlights) ? plan.featureHighlights : [];

  const handlePay = async () => {
    if (!priceConfigured || amount <= 0) return;
    try {
      setSubmitting(true);
      const res = await PaymentService.initiatePayment({ plan: 'agent', billingCycle });
      if (res?.success && res?.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        toast.error(res?.message || 'Failed to start payment.');
        setSubmitting(false);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to start payment.'));
      setSubmitting(false);
    }
  };

  const endDateLabel = formatDate(access.endDate);

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl gradient-bg">
            <FiCreditCard className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Subscription</h1>
            <p className="text-sm text-gray-500">
              Activate your agent subscription to onboard and manage your own sellers.
            </p>
          </div>
        </div>

        {/* Current status */}
        <div
          className={`rounded-2xl border p-5 mb-6 flex items-center gap-4 ${
            access.subscriptionActive
              ? 'border-green-200 bg-green-50'
              : 'border-orange-200 bg-orange-50'
          }`}
        >
          {access.subscriptionActive ? (
            <FiCheckCircle className="h-8 w-8 text-green-500 flex-shrink-0" />
          ) : (
            <FiAlertCircle className="h-8 w-8 text-orange-500 flex-shrink-0" />
          )}
          <div>
            <p className="font-semibold text-gray-900">
              {access.subscriptionActive ? 'Subscription active' : 'No active subscription'}
            </p>
            <p className="text-sm text-gray-600">
              {access.subscriptionActive
                ? endDateLabel
                  ? `Your subscription is active until ${endDateLabel}.`
                  : 'Your subscription is active.'
                : access.paywallEnabled
                ? 'Activate your subscription below to unlock the dashboard and create sellers.'
                : 'Activate your subscription below. Full access is currently open.'}
            </p>
          </div>
        </div>

        {/* Plan card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          {loading ? (
            <div className="py-10 text-center text-gray-500">Loading subscription plan…</div>
          ) : !plan ? (
            <div className="py-10 text-center text-gray-500">
              Subscription plan is not available right now. Please try again later.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {plan.displayName || 'Agent Subscription'}
                  </h2>
                  {plan.description && (
                    <p className="text-sm text-gray-500 mt-1 max-w-md">{plan.description}</p>
                  )}
                </div>
                <div className="text-right">
                  {priceConfigured ? (
                    <>
                      <p className="text-3xl font-bold text-gray-900">
                        ₹{amount.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-gray-400">per {billingCycle.replace('ly', '')}</p>
                    </>
                  ) : (
                    <p className="text-sm font-medium text-orange-600">Pricing coming soon</p>
                  )}
                </div>
              </div>

              {highlights.length > 0 && (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  {highlights.map((f) => (
                    <li key={f} className="flex items-center text-sm text-gray-700">
                      <FiCheck className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              )}

              {priceConfigured ? (
                <>
                  {availableCycles.length > 1 && (
                    <div className="mb-5">
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Billing cycle
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {availableCycles.map((c) => (
                          <button
                            type="button"
                            key={c.id}
                            onClick={() => setBillingCycle(c.id)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                              billingCycle === c.id
                                ? 'gradient-bg text-white border-transparent'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-orange-300'
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handlePay}
                    disabled={submitting}
                    className="w-full gradient-bg text-white font-semibold py-3.5 rounded-xl shadow-md hover:opacity-95 transition disabled:opacity-60 flex items-center justify-center"
                  >
                    {submitting
                      ? 'Redirecting to 1Pay…'
                      : `${access.subscriptionActive ? 'Renew' : 'Pay'} ₹${amount.toLocaleString('en-IN')} via 1Pay`}
                  </button>

                  <p className="text-center text-xs text-gray-400 mt-3">
                    You'll be redirected to 1Pay's secure page. Your subscription activates
                    automatically once the payment succeeds.
                  </p>
                </>
              ) : (
                <div className="rounded-xl bg-gray-50 px-4 py-4 text-center text-sm text-gray-500">
                  Subscription pricing is being finalized. Please check back shortly.
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Subscription;
